import { useAssociatedProducts } from '@/hooks/use-associated-products';
import usePrice from '@/lib/use-price';
import type { ISearchProduct } from '@/types';
import { useMemo } from 'react';
import { flushSync } from 'react-dom';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { Routes } from '@/config/routes';
import {
  MAIN_SEARCH_INDEX_ID,
  TYPESENSE_PUBLISHED_PRODUCTS_FILTER,
} from '@/lib/services/typesense/constants';
import TagGenericoIcon from '@/components/icons/tag-generico-icon';
import ExclamationMarkIcon from '@/components/icons/exclamation-mark-icon';
import { useModalAction } from '@/components/ui/modal/modal.context';
import { skipNextModalHistoryCleanup } from '@/components/ui/modal/history-cleanup';

interface GenericAssociatedDiscountProps {
  product: ISearchProduct;
  associationType?: string;
  className?: string;
  alwaysShow?: boolean; // If true, show UI even without discount data
}

export default function GenericAssociatedDiscount({
  product,
  associationType = 'generico',
  className,
  alwaysShow = false,
}: Readonly<GenericAssociatedDiscountProps>) {
  const router = useRouter();
  const { closeModal } = useModalAction();
  const { data: response, isLoading } = useAssociatedProducts(
    product.sku,
    associationType,
  );

  const associatedProducts = response?.products ?? [];

  const lowestPriceProduct = useMemo(() => {
    if (!associatedProducts || associatedProducts.length === 0) {
      return null;
    }

    // Find the product with the lowest sale_price
    return associatedProducts.reduce((lowest, current) => {
      const currentPrice = current.sale_price || current.price || Infinity;
      const lowestPrice = lowest.sale_price || lowest.price || Infinity;
      return currentPrice < lowestPrice ? current : lowest;
    }, associatedProducts[0]);
  }, [associatedProducts]);

  const discountInfo = useMemo(() => {
    if (!lowestPriceProduct || !product) {
      return null;
    }

    const productPrice = product.sale_price || product.price || 0;
    const lowestPrice = lowestPriceProduct.sale_price || lowestPriceProduct.price || 0;

    if (lowestPrice >= productPrice || productPrice === 0) {
      return null;
    }

    const discountPercentage = ((productPrice - lowestPrice) / productPrice) * 100;

    return {
      discountPercentage,
      lowestPrice,
      productPrice,
    };
  }, [lowestPriceProduct, product]);

  // Always call usePrice hook (hooks must be called unconditionally)
  const { price: lowestPriceFormatted } = usePrice({
    amount: discountInfo?.lowestPrice || 0,
  });

  const handleClick = () => {
    const filterString = `${TYPESENSE_PUBLISHED_PRODUCTS_FILTER} && $associated_products(parent_sku:=${product.sku} && association_type:='${associationType}')`;
    const destination = {
      pathname: Routes.search,
      query: { [`${MAIN_SEARCH_INDEX_ID}[configure][filters]`]: filterString },
    };
    skipNextModalHistoryCleanup();
    flushSync(() => {
      closeModal();
    });
    void router.replace(destination);
  };

  // If alwaysShow is true, show UI even without discount data
  if (alwaysShow) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={classNames(
          'flex items-center gap-3 p-4 bg-white border border-accent rounded-lg cursor-pointer transition-all hover:shadow-md appearance-none text-left w-full',
          className,
        )}
      >
        {/* G Icon */}
        <div className="flex-shrink-0">
          <TagGenericoIcon className="w-14 h-14" />
        </div>

        {/* Text Content */}
        <div className="flex-1 flex flex-col gap-1">
          <p className="text-xl font-light text-gray-700 leading-tight">
            Genéricos
          </p>
          <div className="flex items-center">
            <p className="text-sm font-semibold text-[#F0567A]">
              Compre aqui!
            </p>
            <ExclamationMarkIcon className="w-4 h-4" />
          </div>
        </div>

        {/* Discount Percentage - only show if available */}
        {!isLoading && discountInfo && discountInfo.discountPercentage > 0 && (
          <div className="flex items-center justify-center min-w-[70px] h-10 px-6 rounded-lg">
            <span className="text-green-500 font-bold text-sm">
              -{discountInfo.discountPercentage.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}%
            </span>
          </div>
        )}

        {/* Price - only show if available */}
        {!isLoading && discountInfo && (
          <div className="flex items-center">
            <span className="text-blue-600 font-bold text-base">
              {lowestPriceFormatted}
            </span>
          </div>
        )}
      </button>
    );
  }

  // Original behavior: only show if there's a discount
  if (isLoading) {
    return null;
  }

  if (!discountInfo || discountInfo.discountPercentage <= 0) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={classNames(
        'flex items-center gap-3 p-4 bg-white border border-accent rounded-lg cursor-pointer transition-all hover:shadow-md appearance-none text-left w-full',
        className,
      )}
    >
      {/* G Icon */}
      <div className="flex-shrink-0">
        <TagGenericoIcon className="w-14 h-14" />
      </div>

      {/* Text Content */}
      <div className="flex-1 flex flex-col gap-1">
        <p className="text-xl font-light text-gray-700 leading-tight">
          Genéricos
        </p>
        <div className="flex items-center">
          <p className="text-sm font-semibold whitespace-nowrap text-[#F0567A]">
            Compre aqui!
          </p>
          <ExclamationMarkIcon className="w-4 h-4" />
        </div>
      </div>

      {/* Discount Percentage */}
      <div className="flex items-center justify-center min-w-[70px] h-10 px-6 rounded-lg">
        <span className="text-green-500 font-bold text-sm">
          -{discountInfo.discountPercentage.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}%
        </span>
      </div>

      {/* Price */}
      <div className="flex items-center">
        <span className="text-blue-600 font-bold text-base">
          {lowestPriceFormatted}
        </span>
      </div>
    </button>
  );
}
